import { orderedStudioScenarios } from 'src/components/star-grid/utils/studioScenarioOrder';
import { addStudioScenario } from 'src/components/star-grid/utils/studioScenarioComparison';
import type { NestedRowLayout, ReportSpec } from 'src/components/star-grid/api/reportEngineTypes';
import {
  isNestedRowsPlaceable,
  specFilterBindings,
  studioCatalogWithSpecFilters,
  studioDeckFromSpec
} from 'src/components/star-grid/utils/studioApplySpec';
import {
  MAX_ROW_DIMENSIONS,
  addsRowDimensionBeyondLimit,
  deckAxesFromShelves,
  sameRowDimension,
  savedNestedLayout
} from 'src/components/star-grid/utils/studioSpecFromShelves';
import { studioColumnsFromSpec } from 'src/components/star-grid/utils/studioColumnsFromSpec';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type {
  StudioBinding,
  StudioColumn,
  StudioDeck,
  StudioDefinition,
  StudioField,
  StudioLineSet,
  StudioTemplate
} from 'src/components/star-grid/reportStudio.types';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import {
  includeStudioDecks,
  moveStudioItem,
  removeStudioColumn,
  replaceStudioDeck,
  removeStudioRowField,
  studioDeck,
  studioField,
  studioKey,
  studioVariance
} from 'src/components/star-grid/utils/studioDraft';
import { studioIssues } from 'src/components/star-grid/utils/studioValidation';
import { isStudioBindingSelectable } from 'src/components/star-grid/utils/studioBindings';
import { studioPalette } from 'src/components/star-grid/utils/studioPalette';
import { resolveStudioContext, visibleStudioContext } from 'src/components/star-grid/utils/studioContext';
import { PNL_DEFAULT_SCALE } from 'src/components/star-grid/utils/pnlGrid';
import { applyLineSetToDecks, lineSetTargets } from 'src/components/star-grid/utils/applyLineSetToDecks';
import { deckRowsLineSet, lineSetForField, pickedLineSet } from 'src/components/star-grid/utils/studioFieldLineSet';
import type { PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import { loadStudioHierarchyTree } from 'src/components/star-grid/services/studioHierarchyService';
export const useReportStudio = (template: StudioTemplate, onDirty: (dirty: boolean) => void) => {
  const text = useStarGridText();
  // A new report starts unnamed; the field's placeholder shows the expected format.
  const initialName = template.report_name ?? '';
  const initialDescription = template.report_description ?? '';
  const [draft, setDraft] = useState({ name: initialName, description: initialDescription, definition: template.definition });
  // Re-seed when the starting point changes. Identity, not object equality: `template` is refetched, so
  // comparing objects would discard edits on any incidental re-render.
  const identity = `${template.definition.template_id}:${template.report_id ?? ''}:${template.imported_from?.report_id ?? ''}`;
  const seeded = useRef(identity);
  // Stand-in bindings for spec filters the dataset has no rollup for. Held rather than derived because
  // `applySpec` pushes the spec in once and the shelf must keep resolving those chips afterwards.
  const [specStandIns, setSpecStandIns] = useState<readonly StudioBinding[]>([]);
  // A saved second row dimension the shelf cannot place: shown read-only and carried through save.
  const [nestedRows, setNestedRows] = useState<ReportSpec['row_axis']['nested']>();
  const [rowLimitReached, setRowLimitReached] = useState(false);
  // How a second row dimension lays out: a new one is listed after the first, a saved one keeps its layout.
  const [nestedLayout, setNestedLayout] = useState<NestedRowLayout>('stack');
  useEffect(() => {
    if (seeded.current === identity) return;
    seeded.current = identity;
    setSpecStandIns([]);
    setDraft({ name: initialName, description: initialDescription, definition: template.definition });
  }, [identity, initialName, initialDescription, template.definition]);
  const [scaleId, setScaleId] = useState(PNL_DEFAULT_SCALE.id);
  // Decks a line-set edit applies to. Empty means the active deck only.
  const [lineSetDecks, setLineSetDecks] = useState<readonly string[]>([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('studio');
  const catalog = useMemo(() => studioCatalogWithSpecFilters(template.catalog, specStandIns), [template.catalog, specStandIns]);
  const dirty =
    draft.name !== initialName || draft.description !== initialDescription || studioKey(draft.definition) !== studioKey(template.definition);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);
  // Keyed on `draft.definition` so typing the name recomputes none of them; `palette` memoizes on `deck`.
  const issues = useMemo(() => studioIssues(draft.definition, catalog), [draft.definition, catalog]);
  const deck = useMemo(() => studioDeck(draft.definition), [draft.definition]);
  const dataset = useMemo(
    () => catalog.datasets.find((item) => item.id === draft.definition.dataset_id)!,
    [catalog.datasets, draft.definition.dataset_id]
  );
  const palette = useMemo(() => studioPalette(dataset, deck, search, text), [dataset, deck, search, text]);
  const hasSecondRowDimension = useMemo(() => Boolean(deckAxesFromShelves(deck, dataset, nestedRows).row_axis?.nested), [deck, dataset, nestedRows]);
  const context = useMemo(() => visibleStudioContext(draft.definition, text), [draft.definition, text]);
  const mutateDeck = useCallback(
    (change: (value: StudioDeck) => StudioDeck) =>
      setDraft((value) => ({ ...value, definition: replaceStudioDeck(value.definition, change(studioDeck(value.definition))) })),
    []
  );
  const definition = useCallback(
    (patch: Partial<StudioDefinition>) =>
      setDraft((value) => ({ ...value, definition: resolveStudioContext({ ...value.definition, ...patch }, template.context_references) })),
    [template.context_references]
  );
  // An imported spec is authoritative for the axes, so it replaces both shelves on every deck. The
  // stand-ins keep an unresolvable filter visible: its chip needs a binding to render.
  const applySpec = useCallback(
    (spec: ReportSpec, options?: { readonly savedColumns?: boolean }) => {
      setNestedRows(spec.row_axis?.nested && !isNestedRowsPlaceable(dataset, spec) ? spec.row_axis.nested : undefined);
      setNestedLayout(savedNestedLayout(spec.row_axis));
      const standIns = specFilterBindings(dataset, spec);
      if (standIns.length > 0) setSpecStandIns((current) => [...current, ...standIns]);
      // A deck with its own override is projected from that view, so decks that differ stay different.
      setDraft((value) => {
        // A saved report's columns replace the deck's defaults; a template or deck screen keeps the deck's.
        const columns = options?.savedColumns ? studioColumnsFromSpec(template.catalog, value.definition.dataset_id, spec) : undefined;
        const project = (deck: StudioDeck, view: ReportSpec): StudioDeck => {
          const projected = studioDeckFromSpec(deck, dataset, view);
          return columns ? { ...projected, columns } : projected;
        };
        const decks = Object.keys(value.definition.decks ?? {});
        if (decks.length === 0) return { ...value, definition: replaceStudioDeck(value.definition, project(studioDeck(value.definition), spec)) };
        const next = { ...(value.definition.decks ?? {}) };
        for (const deck of decks) {
          const override = spec.deck_overrides?.[deck];
          next[deck] = project(next[deck], override ? ({ ...spec, ...override } as ReportSpec) : spec);
        }
        return { ...value, definition: { ...value.definition, decks: next } };
      });
    },
    [dataset, template.catalog]
  );
  // The rows pencil always lands on the line set: one is created for a field that has none.
  // The set the pencil was clicked for; the editor lists it first so it is not below another set.
  const [focusedLineSet, setFocusedLineSet] = useState<string>();
  const openLineSet = async (fieldId: string) => {
    const field = deck.rows.find((item) => item.id === fieldId);
    const binding = field && dataset.bindings.find((item) => item.id === field.binding_id);
    if (field?.selection.kind === 'line_set') setFocusedLineSet(field.selection.reference_id);
    if (field && binding && field.selection.kind !== 'line_set') {
      const tree = field.selection.members.length
        ? undefined
        : await loadStudioHierarchyTree(binding, new AbortController().signal).catch(() => undefined);
      const created = lineSetForField(field, binding, tree, deckRowsLineSet(deck, fieldId));
      setFocusedLineSet(created.set.id);
      mutateDeck((value) => ({
        ...value,
        rows: value.rows.map((item) => (item.id === fieldId ? created.field : item)),
        line_sets: [...value.line_sets.filter((item) => item.id !== created.set.id), created.set]
      }));
    }
    setTab('lines');
  };
  // A dimension added to Rows opens its member modal first; what is picked becomes its line set.
  const [pickingRowId, setPickingRowId] = useState<string>();
  const addField = (shelf: 'rows' | 'filters', bindingId: string) => {
    if (!dataset.bindings.some((binding) => binding.id === bindingId) || !isStudioBindingSelectable(dataset, bindingId)) return;
    if (shelf === 'rows') {
      const beyondLimit = addsRowDimensionBeyondLimit(deck, dataset, bindingId, nestedRows);
      setRowLimitReached(beyondLimit);
      if (beyondLimit) return;
    }
    const added = studioField(bindingId);
    if (shelf === 'rows') setPickingRowId(added.id);
    mutateDeck((value) => ({ ...value, [shelf]: [...value[shelf], added] }));
  };
  const finishRowPick = (fieldId: string, members: readonly string[], tree?: PnlHierarchyTree) => {
    setPickingRowId(undefined);
    const field = deck.rows.find((item) => item.id === fieldId);
    const binding = field && dataset.bindings.find((item) => item.id === field.binding_id);
    // Nothing to pick from (a rollup with no member tree): the row stays "All members".
    if (!field || !binding || !members.length) return;
    // Appended to the set the rows shelf already points at, so every row dimension reads as ONE block
    // in the order the dimensions were placed.
    const created = pickedLineSet(field, binding, members, tree, deckRowsLineSet(deck, fieldId));
    mutateDeck((value) => ({
      ...value,
      rows: value.rows.map((item) => (item.id === fieldId ? created.field : item)),
      line_sets: [...value.line_sets.filter((item) => item.id !== created.set.id), created.set]
    }));
  };
  // Cancelling the modal un-adds the row: with nothing picked it would render nothing.
  const cancelRowPick = (fieldId: string) => {
    setPickingRowId(undefined);
    mutateDeck((value) => ({ ...value, rows: value.rows.filter((item) => item.id !== fieldId) }));
  };
  const updateField = (shelf: 'rows' | 'filters', field: StudioField) =>
    mutateDeck((value) => ({ ...value, [shelf]: value[shelf].map((item) => (item.id === field.id ? field : item)) }));
  const sameDimension = (left: string, right: string) => sameRowDimension(dataset, left, right);
  const removeField = (shelf: 'rows' | 'filters', id: string) => {
    if (shelf === 'rows') setRowLimitReached(false);
    mutateDeck((value) => {
      const next =
        shelf === 'rows' ? removeStudioRowField(value, id, sameDimension) : { ...value, filters: value.filters.filter((item) => item.id !== id) };
      return { ...next, columns: next.columns.filter((column) => column.kind !== 'attribute' || column.row_instance_id !== id) };
    });
  };
  const scenarios = useMemo(() => orderedStudioScenarios(template.catalog.scenarios), [template.catalog.scenarios]);
  const addScenario = (id?: string, compareWith?: string) =>
    mutateDeck((value) => addStudioScenario(value, template.catalog, draft.definition, id ?? scenarios[0]?.id, compareWith));
  return {
    text,
    // Set when a saved report was reopened: the save replaces it instead of creating another.
    editingReportId: template.report_id,
    tab,
    setTab,
    draft,
    dataset,
    context,
    palette,
    scenarios,
    deck,
    template,
    issues,
    scaleId,
    setScaleId,
    search,
    setSearch,
    dirty,
    details: (patch: { name?: string; description?: string }) => setDraft((value) => ({ ...value, ...patch })),
    definition,
    includeDecks: (decks: readonly string[]) => setDraft((value) => ({ ...value, definition: includeStudioDecks(value.definition, decks) })),
    applySpec,
    nestedRows,
    nestedLayout,
    setNestedLayout,
    hasSecondRowDimension,
    rowLimitReached,
    rowLimitMessage: text('studioRowsLimit').replace('{max}', String(MAX_ROW_DIMENSIONS)),
    dismissRowLimit: () => setRowLimitReached(false),
    openLineSet,
    focusedLineSet,
    pickingRowId,
    finishRowPick,
    cancelRowPick,
    addField,
    updateField,
    removeField,
    moveField: (shelf: 'rows' | 'filters', index: number, offset: number) =>
      mutateDeck((value) => ({ ...value, [shelf]: moveStudioItem(value[shelf], index, offset) })),
    addScenario,
    addComparison: () => mutateDeck((value) => ({ ...value, columns: [...value.columns, studioVariance(value.columns)] })),
    addAttribute: () =>
      mutateDeck((value) => ({
        ...value,
        columns: [...value.columns, { id: uuid(), kind: 'attribute', label: '', row_instance_id: '', attribute_id: '' }]
      })),
    updateColumn: (column: StudioColumn) =>
      mutateDeck((value) => ({ ...value, columns: value.columns.map((item) => (item.id === column.id ? column : item)) })),
    removeColumn: (id: string) => mutateDeck((value) => removeStudioColumn(value, id)),
    moveColumn: (index: number, offset: number) => mutateDeck((value) => ({ ...value, columns: moveStudioItem(value.columns, index, offset) })),
    /** Which decks a line-set edit lands on. Empty = the active deck only, which is the default. */
    lineSetDecks,
    setLineSetDecks,
    /**
     * Writes an edited line set to the decks in scope, so every kind of edit honours the scope. A deck
     * without the set gets it appended and its rows field repointed, or it would never render it.
     */
    updateLineSet: (set: StudioLineSet) =>
      setDraft((value) => {
        const targets = lineSetTargets(lineSetDecks, value.definition.active_deck);
        if (targets.length === 0) return value;
        return {
          ...value,
          definition: { ...value.definition, decks: applyLineSetToDecks(value.definition.decks ?? {}, targets, set) }
        };
      }),
    /** Empties every shelf; scratch must not keep the previous template's rows and columns. */
    clearDeck: () => mutateDeck(() => ({ rows: [], filters: [], columns: [], line_sets: [] }))
  };
};
export type ReportStudioState = ReturnType<typeof useReportStudio>;
